import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustodianService } from 'src/app/services/custodian.service';
import { DocService } from 'src/app/services/doc.service';
import { FilterFieldGroup } from 'src/app/components/dialogs/select-filter-new/models/filter-field-group';
import { FieldDisplayType } from 'src/app/components/dialogs/select-filter-new/models/field-display-type';
import { FilterService } from 'src/app/services/filter.service';
import { ReviewSetService } from 'src/app/services/review-set.service';
import { KeyHandlerDirective } from '../../../directives/key-handler.directive';
import { SelectFilterComponent } from '../select-filter/select-filter.component';
import { FieldSearchComponent } from './field-search/field-search.component';
import { AdvancedSelectionManager } from './util/advanced-selection-manager';
import { DynamicChoiceRepository } from './util/dynamic-choice-repository';
import { FieldDeserializer } from './util/field-deserializer';
import { FilterMatch, FilterType, SavedFilter } from './models/saved-filter';
import { SelectionManager } from './util/selection-manager';
import { FilterComparerGuids } from './models/filter-comparer-guids';
import { FieldValue } from './models/field-value';
import { AdvancedFieldValue } from './models/advanced-field-value';
import { AutoRedactionService } from '../../../services/auto-redaction-service/auto-redaction.service';
import { FilterFieldGuids } from './models/filter-field-guids';
import { UserManager } from '../../../managers/user.manager';
import { UploadBatchesSdk } from "../../../sdks/upload-batches.sdk";
import { ProductionJobSdk } from 'src/app/sdks/production-job.sdk';
import { BriefcaseJobSdk } from "../../../sdks/briefcase-job.sdk";
import { FactSdk } from '@sdks/fact.sdk';

@Component({
    selector: 'lex-select-filter-new',
    templateUrl: './select-filter-new.component.html',
    styleUrls: ['./select-filter-new.component.scss'],
    standalone: false
})
export class SelectFilterNewComponent extends KeyHandlerDirective implements OnInit {
	public groups: FilterFieldGroup[] = [];
	public FieldDisplayType = FieldDisplayType;
	public choiceRepo: DynamicChoiceRepository;
	public deserializer: FieldDeserializer;
	public existingFilter: SavedFilter;
	public selectionManager: SelectionManager;
	public advancedSelectionManager: AdvancedSelectionManager;
	public match = FilterMatch.All;
	public levelOneNot = false;
	selectedTab = 0;
	public simpleDisabled = false;
	public FilterMatch = FilterMatch;
	public FilterComparerGuids = FilterComparerGuids;
	public advancedError: string;
	public FilterFieldGuids = FilterFieldGuids;

	@ViewChild('simpleFieldSearch') simpleFieldSearch: FieldSearchComponent;
	@ViewChild('advancedFieldSearch') advancedFieldSearch: FieldSearchComponent;

	constructor(
		@Inject(MAT_DIALOG_DATA) data,
		public custodianService: CustodianService,
		public userManager: UserManager,
		public uploadBatchSdk: UploadBatchesSdk,
		public reviewSetService: ReviewSetService,
		public productionJobSdk: ProductionJobSdk,
		public docService: DocService,
		private dialogRef: MatDialogRef<SelectFilterComponent>,
		private filterService: FilterService,
		private autoRedactionService: AutoRedactionService,
		private briefcaseJobSdk: BriefcaseJobSdk,
		private factSdk: FactSdk
	) {
		super();
		this.choiceRepo = new DynamicChoiceRepository(this.autoRedactionService, custodianService, userManager, uploadBatchSdk, reviewSetService, productionJobSdk, docService, briefcaseJobSdk,factSdk);
		this.selectionManager = new SelectionManager(this.choiceRepo);
		this.advancedSelectionManager = new AdvancedSelectionManager(this.choiceRepo);
		this.deserializer = new FieldDeserializer(this.choiceRepo);
		this.existingFilter = data.filter;
		if ((this.existingFilter !== null && this.existingFilter.type === null) || this.existingFilter?.type === FilterType.Advanced) {
			this.selectedTab = 1;
			this.simpleDisabled = true;
		}
	}

	ngOnInit(): void {
		this.filterService.getFilterFields().subscribe(data => {
			this.groups = data;
			this.selectionManager.setFields(this.groups);
			if (this.existingFilter) {
				const allFields = this.groups.reduce((all, group) => all.concat(group.fields), []);
				const filterUniqueIds = new Set<string>;
				if (this.existingFilter?.type == null || this.existingFilter?.type === FilterType.Advanced) {
					this.match = this.existingFilter.match;
					this.levelOneNot = this.existingFilter.not;
					this.existingFilter.fields.forEach((f,idx) => {
						if (f.displayType === FieldDisplayType.SearchReport && JSON.parse(f.value.shift()))
							filterUniqueIds.add(`${f.fieldId}_${idx}`);
					});
					this.advancedSelectionManager.seedUniqueFilterHits(filterUniqueIds);
					this.advancedSelectionManager.deserialize(allFields, this.existingFilter);
				} else {
					this.existingFilter.fields.forEach(f => {
						if (f.displayType === FieldDisplayType.SearchReport && JSON.parse(f.value.shift()))
							filterUniqueIds.add(f.fieldId);
					});
					this.selectionManager.seedUniqueFilterHits(filterUniqueIds);
					this.deserializer.deserialize(allFields, this.existingFilter, this.selectionManager.selectedFields);
				}
			}
		});
	}

	public onTabChange() {
		if (this.selectedTab === 1 && this.advancedSelectionManager.selectedFields.length === 0) {
			this.advancedSelectionManager.seedUniqueFilterHits(this.selectionManager.getSelectedUniqueReportIds());
			this.selectionManager.selectedFields.forEach(f => 
				this.advancedSelectionManager.addField(AdvancedFieldValue.fromSimple(f)));
		}
	}

	public optionSelected(option: any) {
		// If the user selected an option using the Enter key, override the dialog close to let them interact with the field first
		super.setOverride(true);

		// Reset the filtered observable so that it returns to the full set of options
		// (after an option is selected the filtered observable is left with whatever matched the user's input)
		this.simpleFieldSearch.setFilteredObservable();

		this.simpleFieldSearch.unFocus();

		this.selectionManager.optionSelected(option);
	}

	public optionSelectedAdvanced(option: any) {
		// If the user selected an option using the Enter key, override the dialog close to let them interact with the field first
		super.setOverride(true);

		// Reset the filtered observable so that it returns to the full set of options
		// (after an option is selected the filtered observable is left with whatever matched the user's input)
		this.advancedFieldSearch.setFilteredObservable();

		this.advancedFieldSearch.unFocus();

		this.advancedSelectionManager.optionSelected(option);
	}

	public optionSelectedGroup(option: any, idx: number) {
		// If the user selected an option using the Enter key, override the dialog close to let them interact with the field first
		super.setOverride(true);

		this.advancedSelectionManager.optionSelectedGroup(option, idx);
	}

	public selectAll(fv: FieldValue) {
		fv.value = [];
		if (fv.field.displayType !== FieldDisplayType.SearchReport)
			fv.value = this.choiceRepo.findChoicesFor(fv.field).map(v => v.value);
		else
			fv.value = fv.field.choices.map(c => c.value);
	}

	public ok() {
		if (this.selectedTab === 0) { // Simple
			const savedFilters = this.selectionManager.serialize();

			this.dialogRef.close({
				isCancelled: false,
				filter: this.selectionManager.selectedFields.length > 0 ? savedFilters : null
			});
		} else { // Advanced
			if (!this.advancedSelectionManager.areDatesValid()) {
				this.advancedError = 'Date ranges require two valid values.';
				return;
			} else {
				this.advancedError = '';
			}

			const payload: SavedFilter = {
				type: FilterType.Advanced,
				match: this.match,
				not: this.levelOneNot,
				fields: this.advancedSelectionManager.serialize()
			};

			this.dialogRef.close({
				isCancelled: false,
				filter: payload.fields.length > 0 ? payload : null
			});
		}
	}

	public cancel() {
		this.dialogRef.close({
			isCancelled: true,
			filter: null
		});
	}
}
